import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Sparkles, Target, Users, Calendar, Video, CheckCircle2, 
  ArrowRight, Play, FileText, Clock, Star, Zap, Award, ExternalLink, User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InnerEdgeHubPage = () => {
  const { user, hasTier } = useAuth();
  const [hubData, setHubData] = useState(null);
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [matchedCoach, setMatchedCoach] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHubData();
  }, []);

  const fetchHubData = async () => {
    try {
      const [hubRes, diagnosticRes] = await Promise.all([
        axios.get(`${API_URL}/api/inner-edge/hub`),
        axios.get(`${API_URL}/api/diagnostic/results`).catch(() => ({ data: null }))
      ]);
      setHubData(hubRes.data);
      setDiagnosticResults(diagnosticRes.data);
      // Get matched coach from hub data
      if (hubRes.data?.matched_coach) {
        setMatchedCoach(hubRes.data.matched_coach);
      }
    } catch (error) {
      console.error('Failed to fetch Inner Edge hub:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookChemistry = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/sessions/book/chemistry`);
      if (response.data.calendly_link) {
        window.open(response.data.calendly_link, '_blank');
        toast.success('Opening scheduling page...');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book chemistry session');
    }
  };

  const handleBookSession = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/sessions/book/matched-coach`);
      if (response.data.calendly_link) {
        window.open(response.data.calendly_link, '_blank');
        toast.success('Opening scheduling page...');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book session');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
        </div>
      </DashboardLayout>
    );
  }

  const sessionsRemaining = user?.inner_edge_sessions_remaining || hubData?.sessions_remaining || 4;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="inner-edge-hub-page">
        {/* Header with Community Image */}
        <div className="mb-8">
          <div className="relative rounded-2xl overflow-hidden mb-6">
            <img 
              src="https://images.unsplash.com/photo-1573165706511-3ffde6ef1fe3?w=1400&q=80"
              alt="Sister Diamonds Inner Edge Community"
              className="w-full h-72 md:h-80 object-cover object-[center_15%]"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-forest/80 to-transparent flex items-center">
              <div className="p-6">
                <h1 className="font-heading text-3xl font-bold text-white mb-2">Inner Edge Hub</h1>
                <p className="text-white/90 max-w-md">Your private space for transformational leadership development with your sisters.</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-forest to-terracotta flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div>
              <Badge className="bg-forest/10 text-forest">
                Leadership Deep Dive Program
              </Badge>
              <p className="text-sm text-gray-500 mt-1">Growing together in brilliance</p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="card-base">
            <CardContent className="p-4 text-center">
              <Target className="h-6 w-6 text-terracotta mx-auto mb-2" />
              <p className="text-2xl font-bold text-forest">{diagnosticResults ? '1' : '0'}/1</p>
              <p className="text-xs text-gray-500">Diagnostic Complete</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-4 text-center">
              <Calendar className="h-6 w-6 text-terracotta mx-auto mb-2" />
              <p className="text-2xl font-bold text-forest">{sessionsRemaining}</p>
              <p className="text-xs text-gray-500">Sessions Remaining</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-4 text-center">
              <Video className="h-6 w-6 text-terracotta mx-auto mb-2" />
              <p className="text-2xl font-bold text-forest">4</p>
              <p className="text-xs text-gray-500">Power Circles</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-4 text-center">
              <Star className="h-6 w-6 text-terracotta mx-auto mb-2" />
              <p className="text-2xl font-bold text-forest">1</p>
              <p className="text-xs text-gray-500">C-Sweet Session</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-sand/50">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="diagnostic">Diagnostic</TabsTrigger>
            <TabsTrigger value="circles">Power Circles</TabsTrigger>
            <TabsTrigger value="coaching">Coaching</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Program Progress */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Zap className="h-5 w-5 text-terracotta" />
                    Your Journey
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-sand/30">
                    {diagnosticResults ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-forest">Complete Diagnostic</p>
                      <p className="text-xs text-gray-500">Discover your leadership style</p>
                    </div>
                    {!diagnosticResults && (
                      <Link to="/diagnostic">
                        <Button size="sm" className="btn-primary text-xs">
                          Start <ArrowRight className="ml-1 h-3 w-3" />
                        </Button>
                      </Link>
                    )}
                  </div>

                  <div className="flex items-center gap-3 p-3 rounded-lg bg-sand/30">
                    <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                    <div className="flex-1">
                      <p className="font-medium text-forest">Book Coaching Sessions</p>
                      <p className="text-xs text-gray-500">{sessionsRemaining} of 4 sessions available</p>
                    </div>
                    <Link to="/sessions">
                      <Button size="sm" variant="outline" className="text-xs">
                        Book
                      </Button>
                    </Link>
                  </div>

                  <div className="flex items-center gap-3 p-3 rounded-lg bg-sand/30">
                    <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                    <div className="flex-1">
                      <p className="font-medium text-forest">Attend Power Circles</p>
                      <p className="text-xs text-gray-500">4 live sessions + replays</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 rounded-lg bg-sand/30">
                    <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                    <div className="flex-1">
                      <p className="font-medium text-forest">C-Sweet Consultation</p>
                      <p className="text-xs text-gray-500">1:1 with Crystal or Dr. Nicole</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Diagnostic Summary */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Target className="h-5 w-5 text-terracotta" />
                    Your Leadership Profile
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {diagnosticResults ? (
                    <div className="space-y-4">
                      <div className="p-4 bg-forest text-white rounded-xl text-center">
                        <p className="text-sm opacity-80">Primary Style</p>
                        <p className="text-2xl font-heading font-bold">{diagnosticResults.leadership_style}</p>
                      </div>
                      <div className="p-4 bg-terracotta/10 rounded-xl">
                        <p className="text-sm text-gray-600">Stress Response</p>
                        <p className="font-semibold text-forest">{diagnosticResults.stress_response}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Focus Areas</p>
                        <div className="flex flex-wrap gap-2">
                          {diagnosticResults.focus_areas?.map((area, i) => (
                            <Badge key={i} className="bg-sand text-forest">{area}</Badge>
                          ))}
                        </div>
                      </div>
                      <Link to="/diagnostic">
                        <Button variant="outline" className="w-full btn-outline">
                          View Full Results
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Target className="h-12 w-12 text-terracotta/30 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">Complete your diagnostic to see your leadership profile</p>
                      <Link to="/diagnostic">
                        <Button className="btn-primary">
                          Start Diagnostic <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Diagnostic Tab */}
          <TabsContent value="diagnostic">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest">Inner Edge Diagnostic</CardTitle>
                <CardDescription>
                  A comprehensive assessment to understand your leadership style, stress patterns, and growth opportunities.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {diagnosticResults ? (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="p-6 bg-forest text-white rounded-xl">
                        <p className="text-sm opacity-80 mb-1">Your Leadership Style</p>
                        <p className="text-3xl font-heading font-bold">{diagnosticResults.leadership_style}</p>
                      </div>
                      <div className="p-6 bg-terracotta/10 rounded-xl">
                        <p className="text-sm text-gray-600 mb-1">Stress Response Pattern</p>
                        <p className="text-2xl font-heading font-semibold text-forest">{diagnosticResults.stress_response}</p>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-heading text-lg text-forest mb-3">Style Scores</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {Object.entries(diagnosticResults.style_scores || {}).map(([style, score]) => (
                          <div key={style} className="p-3 bg-sand/30 rounded-lg">
                            <p className="text-sm text-gray-600">{style}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Progress value={(score / 10) * 100} className="h-2 flex-1" />
                              <span className="text-sm font-medium text-forest">{score}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="font-heading text-lg text-forest mb-3">Recommendations</h3>
                      <div className="space-y-3">
                        {diagnosticResults.recommendations?.map((rec, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 bg-sand/30 rounded-lg">
                            <span className="h-6 w-6 rounded-full bg-terracotta text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                              {i + 1}
                            </span>
                            <p className="text-gray-700">{rec}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Only show retake if no coach has been assigned */}
                    {!hubData?.coach && (
                      <Link to="/diagnostic">
                        <Button variant="outline" className="btn-outline">
                          Retake Diagnostic
                        </Button>
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="h-20 w-20 rounded-full bg-terracotta/10 flex items-center justify-center mx-auto mb-6">
                      <Target className="h-10 w-10 text-terracotta" />
                    </div>
                    <h3 className="font-heading text-xl text-forest mb-2">Discover Your Leadership Style</h3>
                    <p className="text-gray-500 mb-6 max-w-md mx-auto">
                      Take the Inner Edge Diagnostic to uncover your unique leadership strengths, 
                      stress patterns, and personalized growth recommendations.
                    </p>
                    <Link to="/diagnostic">
                      <Button className="btn-primary">
                        Start Diagnostic <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Power Circles Tab */}
          <TabsContent value="circles">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                  <Users className="h-5 w-5 text-terracotta" />
                  Power &amp; Presence Circles
                </CardTitle>
                <CardDescription>
                  4 transformational group sessions designed to elevate your leadership presence.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { title: 'Circle 1: Owning Your Authority', date: 'Coming Soon', description: 'Claim your space and lead with confidence.' },
                    { title: 'Circle 2: Navigating Power Dynamics', date: 'Coming Soon', description: 'Master the art of influence and strategic positioning.' },
                    { title: 'Circle 3: Presence Under Pressure', date: 'Coming Soon', description: 'Stay grounded and powerful in high-stakes moments.' },
                    { title: 'Circle 4: Legacy & Impact', date: 'Coming Soon', description: 'Define and create the leadership legacy you desire.' }
                  ].map((circle, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 border border-sand rounded-xl hover:border-terracotta/30 transition-colors">
                      <div className="h-12 w-12 rounded-xl bg-forest text-white flex items-center justify-center font-heading font-bold text-lg flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-heading font-semibold text-forest">{circle.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{circle.description}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                          <Clock className="h-3 w-3" />
                          <span>{circle.date}</span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" disabled className="text-xs">
                        <Play className="h-3 w-3 mr-1" /> Replay
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Coaching Tab */}
          <TabsContent value="coaching">
            <div className="space-y-6">
              {/* Matched Coach Card */}
              {matchedCoach ? (
                <Card className="card-base border-2 border-terracotta/30 bg-gradient-to-br from-terracotta/5 to-forest/5">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-2">
                      <Award className="h-5 w-5 text-terracotta" />
                      <CardTitle className="font-heading text-xl text-forest">
                        Your Assigned Coach
                      </CardTitle>
                    </div>
                    <CardDescription>
                      Based on your diagnostic results, this coach has been specifically matched to your needs
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex flex-col sm:flex-row gap-6">
                      {/* Coach Photo */}
                      <div className="flex-shrink-0">
                        <div className="h-28 w-28 rounded-2xl overflow-hidden border-4 border-white shadow-lg mx-auto sm:mx-0">
                          <img 
                            src={matchedCoach.image_url} 
                            alt={matchedCoach.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=300'; }}
                          />
                        </div>
                      </div>
                      
                      {/* Coach Info */}
                      <div className="flex-1 text-center sm:text-left">
                        <h3 className="font-heading text-xl font-bold text-forest mb-1">
                          {matchedCoach.name}
                        </h3>
                        <p className="text-terracotta font-medium text-sm mb-2">{matchedCoach.title}</p>
                        <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
                          {matchedCoach.bio}
                        </p>
                        {matchedCoach.specialties && (
                          <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
                            {matchedCoach.specialties.slice(0, 3).map((specialty, i) => (
                              <span key={i} className="text-xs px-2 py-1 bg-sand text-forest rounded-full">
                                {specialty}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Session Remaining Info */}
                    <div className="bg-sand/50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Coaching Sessions Remaining</p>
                          <p className="text-2xl font-bold text-forest">{sessionsRemaining}</p>
                        </div>
                        <Calendar className="h-8 w-8 text-terracotta" />
                      </div>
                    </div>

                    {/* Booking Button */}
                    <Button 
                      onClick={handleBookSession}
                      className="w-full btn-primary"
                      disabled={sessionsRemaining <= 0}
                      data-testid="hub-book-session-btn"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      Book Session with {matchedCoach.name.split(' ')[0]}
                    </Button>

                    <p className="text-xs text-gray-500 text-center">
                      Inner Edge members are matched with a dedicated coach based on their diagnostic results. 
                      All coaching sessions are with your assigned coach.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="card-base">
                  <CardContent className="p-8 text-center">
                    <div className="h-16 w-16 rounded-full bg-terracotta/10 flex items-center justify-center mx-auto mb-4">
                      <User className="h-8 w-8 text-terracotta" />
                    </div>
                    <h3 className="font-heading text-xl text-forest mb-2">Complete Your Diagnostic First</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      Take the Inner Edge Diagnostic to be matched with your ideal coach based on your unique leadership profile and development needs.
                    </p>
                    <Link to="/diagnostic">
                      <Button className="btn-primary">
                        Start Diagnostic <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}

              {/* No Coach Match Yet */}
              {!matchedCoach && diagnosticResults && (
                <Card className="card-base border-dashed border-2 border-sand">
                  <CardContent className="p-8 text-center">
                    <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="font-heading text-lg text-forest mb-2">Coach Matching in Progress</h3>
                    <p className="text-gray-500 text-sm">
                      Your coach match is being processed. Check back soon!
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Complete Diagnostic Prompt */}
              {!diagnosticResults && (
                <Card className="card-base border-2 border-terracotta/20">
                  <CardContent className="p-8 text-center">
                    <Target className="h-12 w-12 text-terracotta/50 mx-auto mb-4" />
                    <h3 className="font-heading text-lg text-forest mb-2">Complete Your Diagnostic</h3>
                    <p className="text-gray-500 text-sm mb-4">
                      Take the Inner Edge diagnostic to get matched with your ideal coach.
                    </p>
                    <Link to="/diagnostic">
                      <Button className="btn-primary">
                        Start Diagnostic <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}

              <div className="grid lg:grid-cols-2 gap-6">
                {/* Session Wallet */}
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-terracotta" />
                      Your Coaching Sessions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 bg-forest text-white rounded-xl">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm opacity-80">Coach Sessions</p>
                          <p className="text-3xl font-heading font-bold">{sessionsRemaining}</p>
                          <p className="text-xs opacity-60">of 4 remaining</p>
                        </div>
                        {matchedCoach ? (
                          <Button onClick={handleBookSession} className="bg-white text-forest hover:bg-sand">
                            Book Session
                          </Button>
                        ) : (
                          <Link to="/sessions">
                            <Button className="bg-white text-forest hover:bg-sand">
                              View Coaches
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>

                    <div className="p-4 bg-terracotta/10 rounded-xl">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm text-gray-600">C-Sweet Consultation</p>
                          <p className="text-2xl font-heading font-bold text-forest">1</p>
                          <p className="text-xs text-gray-400">with Crystal or Dr. Nicole</p>
                        </div>
                        <Button variant="outline" className="btn-outline">
                          Schedule
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Eligible Coaches */}
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                      <Star className="h-5 w-5 text-terracotta" />
                      Your Coach Network
                    </CardTitle>
                    <CardDescription>
                      Inner Edge gives you access to our network of black female executive coaches.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      Connect with coaches who understand your unique journey as a leader.
                    </p>
                    <Link to="/sessions">
                      <Button className="btn-primary w-full">
                        View All Eligible Coaches <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default InnerEdgeHubPage;
